// vintage-fair — b07 "Tomosha" milestone birthday preview card.
//
// 1920s-fair broadside compressed into a poster-card. A double-keyline frame
// in crimson on cream, a striped circus-tent rosette in the centre with a
// ferris-wheel sunburst, the celebrant's name in Playfair Display.
import { Photo, type LayoutCtx } from "./_shared";

export function VintageFairLayout({ names, date, venue, accent, ink, bg, font, photo }: LayoutCtx) {
  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        background: `linear-gradient(180deg, ${accent}1F 0%, ${bg} 30%, ${bg} 70%, ${accent}1F 100%)`,
        padding: "5%",
        display: "flex",
      }}
    >
      <div
        style={{
          flex: 1,
          position: "relative",
          background: bg,
          color: ink,
          padding: "8%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          boxShadow: `inset 0 0 0 1px ${accent}88, inset 0 0 0 3px ${bg}, inset 0 0 0 4px ${accent}55`,
        }}
      >
        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 7,
            fontWeight: 800,
            letterSpacing: "0.4em",
            color: accent,
            marginBottom: "5%",
          }}
        >
          TUG'ILGAN KUN
        </div>

        {/* Fair rosette: ferris-wheel sunburst with a striped tent */}
        <div style={{ width: "62%", aspectRatio: "1 / 1", marginBottom: "5%", position: "relative" }}>
          <Photo
            src={photo}
            style={{ position: "absolute", inset: "14%", borderRadius: "50%" }}
            fallback={
              <svg viewBox="0 0 100 100" style={{ width: "100%", height: "100%", display: "block" }}>
                {/* Sunburst behind */}
                <g fill={accent} opacity="0.18">
                  {Array.from({ length: 24 }).map((_, i) => (
                    <polygon
                      key={i}
                      points="50,50 48.5,4 51.5,4"
                      transform={`rotate(${(i * 360) / 24} 50 50)`}
                    />
                  ))}
                </g>
                {/* Outer ring (ferris wheel) */}
                <circle cx="50" cy="50" r="40" fill="none" stroke={accent} strokeWidth="0.9" />
                <circle cx="50" cy="50" r="33" fill="none" stroke={accent} strokeWidth="0.4" opacity="0.5" />
                {/* Spokes */}
                <g stroke={accent} strokeWidth="0.5" opacity="0.7">
                  {Array.from({ length: 12 }).map((_, i) => {
                    const a = (i / 12) * Math.PI * 2;
                    return (
                      <line
                        key={i}
                        x1="50"
                        y1="50"
                        x2={50 + Math.cos(a) * 40}
                        y2={50 + Math.sin(a) * 40}
                      />
                    );
                  })}
                </g>
                {/* Striped circus tent (centre) */}
                <g transform="translate(50 56)">
                  <path d="M-18 6 L -18 -4 L 0 -22 L 18 -4 L 18 6 Z" fill={bg} stroke={accent} strokeWidth="0.8" />
                  {/* Stripes */}
                  {[-12, -6, 0, 6, 12].map((x) => (
                    <line key={x} x1={x} y1="6" x2={x * 0.4} y2="-18"
                          stroke={accent} strokeWidth="0.7" opacity="0.85" />
                  ))}
                  {/* Pennant */}
                  <line x1="0" y1="-22" x2="0" y2="-30" stroke={accent} strokeWidth="0.6" />
                  <path d="M0 -30 L 8 -27 L 0 -24 Z" fill={accent} />
                  {/* Door arch */}
                  <path d="M-5 6 L -5 -2 Q 0 -7 5 -2 L 5 6 Z" fill={accent} opacity="0.75" />
                </g>
                {/* Tiny popcorn stars */}
                {[
                  { x: 18, y: 22 }, { x: 82, y: 24 }, { x: 22, y: 78 }, { x: 78, y: 80 },
                ].map((s, i) => (
                  <g key={i} transform={`translate(${s.x} ${s.y})`} fill={accent} opacity="0.7">
                    <path d="M0 -3 L 1 -1 L 3 0 L 1 1 L 0 3 L -1 1 L -3 0 L -1 -1 Z" />
                  </g>
                ))}
              </svg>
            }
          />
        </div>

        <div
          style={{
            fontFamily: font,
            fontSize: 30,
            lineHeight: 1.05,
            color: ink,
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: "0.02em",
          }}
        >
          {names}
        </div>

        <div style={{ flex: 1 }} />

        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 6.5,
            fontWeight: 800,
            letterSpacing: "0.32em",
            color: accent,
            marginTop: "4%",
          }}
        >
          TUG'ILGAN KUN · {date}
        </div>
        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 6,
            fontWeight: 600,
            letterSpacing: "0.28em",
            color: `${ink}99`,
            marginTop: 6,
          }}
        >
          {venue.toUpperCase()}
        </div>
      </div>
    </div>
  );
}
